/**
 * File: apps/qbench-cp/reactapp/src/App/routes.js
 *
 * Purpose: Declares the customer portal's client-side routes and the lazy-loaded page
 *     components they render.
 *
 * Scope: Route definitions for the authenticated portal (orders, invoices, quotations,
 *     sources, customer, settings, help center) plus a small set of public routes.
 *
 * Tags: customer-portal
 *
 * Overview: Lazily imports each page component and defines a `publicRoutes` array used
 *     elsewhere for routes reachable without authentication (quotation preview, tokenized
 *     sign-up). The `Content` component reads app state (billingEnabled, delinquent) and the
 *     current-user query (enforce_mfa_for_portal, two_fa_enabled) to redirect users who owe an
 *     MFA setup step to the Settings page instead of their intended destination, and to gate
 *     billing- and delinquency-sensitive routes behind NotFound when the corresponding feature
 *     or account state disallows access.
 *
 * Exports: Content — default export, the `<Routes>` tree mounted for the authenticated portal.
 *     publicRoutes — named export, route descriptors for unauthenticated access.
 */

import { lazy } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { useAppStore } from 'App/store';
import { useMeQuery } from 'shared/hooks/api/contacts.js';

const Home = lazy(() => import('../Dashboard/Pages/Home/Home.jsx'));
const CreateOrder = lazy(() => import('../Order/index'));
const Orders = lazy(() => import('../Dashboard/Pages/Orders/Orders'));
const OrderDetails = lazy(() =>
	import('../Dashboard/Pages/Orders/OrderDetails/OrderDetails')
);
const Invoices = lazy(() => import('../Dashboard/Pages/Invoices/Invoices'));
const InvoiceDetails = lazy(() => import('../Dashboard/Pages/Invoices/InvoiceDetails/InvoiceDetails'));
const Quotations = lazy(() => import('../Dashboard/Pages/Quotations/QuotationList'));
const QuotationDetails = lazy(() => import('../Dashboard/Pages/Quotations/QuotationDetails'));
const QuotationPreview = lazy(() => import('../Dashboard/Pages/Quotations/Preview'));
const SourceList = lazy(() => import('../Dashboard/Pages/Sources/SourceList/SourceList.js'));
const SourceDetails = lazy(() => import('../Dashboard/Pages/Sources/SourceDetails/SourceDetails.js'));
const TokenizedSignUp = lazy(() => import('../SignUp/TokenizedSignUp'));
const HelpCenter = lazy(() => import('../Dashboard/Pages/HelpCenter/DocumentList'));
const Profile = lazy(() => import('../Dashboard/Pages/Settings/Profile.js'));
const Settings = lazy(() => import('../Dashboard/Pages/Settings/UserSettings.js'));
const CustomerDetails = lazy(() => import('../Dashboard/Pages/Customers/CustomerDetails/CustomerDetails'));
const NotFound = lazy(() => import('shared/layout/NotFound'));

export const publicRoutes = [
  {
    path: '/portal/quotations/preview',
    element: <QuotationPreview />,
  },
  {
    path: '/portal/quotations/preview/:uuid',
    element: <QuotationPreview />,
  },
  {
    path: '/portal/onboard',
    element: <TokenizedSignUp />,
  },
];

const Content = () => {
	const { appState: { billingEnabled, delinquent } } = useAppStore();
	const [{enforce_mfa_for_portal, two_fa_enabled}] = useMeQuery();
	return (
		<Routes>
			<Route path='/portal' exact element={enforce_mfa_for_portal && !two_fa_enabled ?<Settings/> : <Home/>} />
			<Route path='/portal/home' element={<Navigate to='/portal' />} />
			<Route path='/portal/createorder' element={enforce_mfa_for_portal && !two_fa_enabled ?<Settings/> : (!delinquent ? <CreateOrder/>: <NotFound/>)} />
			<Route path="/portal/createorder/:id" element={enforce_mfa_for_portal && !two_fa_enabled ?<Settings/> : (!delinquent ? <CreateOrder/>: <NotFound/>)} />
			<Route path='/portal/orders' exact element={enforce_mfa_for_portal && !two_fa_enabled ?<Settings/> : <Orders/>} />
			<Route path='/portal/orders/:id' element={enforce_mfa_for_portal && !two_fa_enabled ?<Settings/> : <OrderDetails/>} />
			<Route path='/portal/invoices' exact element={enforce_mfa_for_portal && !two_fa_enabled ?<Settings/> : (billingEnabled ? <Invoices/> : <NotFound/>)} />
			<Route path='/portal/invoices/:id' element={enforce_mfa_for_portal && !two_fa_enabled ?<Settings/> : (billingEnabled ? <InvoiceDetails/> : <NotFound/>)} />
			<Route path='/portal/quotations' exact element={enforce_mfa_for_portal && !two_fa_enabled ?<Settings/> : (billingEnabled ? <Quotations/> : <NotFound />)} />
			<Route path='/portal/quotations/:id' element={enforce_mfa_for_portal && !two_fa_enabled ?<Settings/> : (billingEnabled ? <QuotationDetails/> : <NotFound />)} />
			<Route path='/portal/sources' exact element={enforce_mfa_for_portal && !two_fa_enabled ? <Settings /> : <SourceList />} />
			<Route path='/portal/sources/:id' element={enforce_mfa_for_portal && !two_fa_enabled ? <Settings /> : <SourceDetails />} />
			<Route path='/portal/profile' exact element={<Profile/>} />
			<Route path='/portal/settings' exact element={<Settings/>} />
			<Route path="/portal/customer">
				<Route path="users/:id" element={enforce_mfa_for_portal && !two_fa_enabled ? <Settings /> : <CustomerDetails />} />
				<Route path="users" element={enforce_mfa_for_portal && !two_fa_enabled ? <Settings /> : <CustomerDetails />} />
				<Route index element={enforce_mfa_for_portal && !two_fa_enabled ? <Settings /> : <CustomerDetails />} />
			</Route>
			<Route path='/portal/helpcenter' exact element={<HelpCenter />} />
			<Route exact path='/' element={<Navigate to='/portal' />} />
			<Route path='*' element={<NotFound/>} />
		</Routes>
	);
};

export default Content;
